import React from 'react'

import { scrollbarStyle } from '@/shared/styles'
import { pxToRem } from '@/shared/styles/themes'
import { faTruck } from '@fortawesome/pro-solid-svg-icons'
import { CircularProgress, Grid, styled, Tooltip, Typography } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { CurrentTimeLine } from './CurrentTimeline'
import { useGanttContext } from './GanttContext'
import { GanttLeg } from './GanttLeg'
import { GanttStop } from './GanttStop'
import { useTimelineMarkers } from './libs/generateTimelineMarkers'
import { GanttRow, TimelineStop } from './models/GanttRow'

export type GanttChartProps = {
  rows: GanttRow[]
  startHour?: number
  endHour?: number
  defaultZoom?: number
  showTimeline?: boolean
  loading?: boolean
  onStopDrop: (sourceStop: TimelineStop, targetStop: TimelineStop) => void
}

export const GanttChart: React.FC<GanttChartProps> = ({
  rows,
  startHour = 7,
  endHour = 19,
  showTimeline,
  loading,
  onStopDrop,
}) => {
  const { zoom } = useGanttContext()
  const [majorMarkers, subMarkers] = useTimelineMarkers(startHour, endHour, zoom)

  const isTimelineShown = () => {
    if (!showTimeline) return false

    const now = new Date()
    const currentHour = now.getHours()

    return currentHour >= startHour && currentHour <= endHour
  }

  const totalHours = endHour - startHour
  const baseWidth = 100
  const timelineWidth = baseWidth * totalHours * zoom
  const intervalWidth = (timelineWidth / (majorMarkers.length - 1))

  return (
    <StyledContainer>
      <StyledScrollContainer>
        <StyledTable>
          <StyledHeaderRow>
            <StyledFixedCell>
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Scenario</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Run</Typography>
                </Grid>
              </Grid>
            </StyledFixedCell>

            <StyledTimelineCell>
              <StyledTimelineContent $width={timelineWidth}>
                <StyledTimelineHeader>
                  {majorMarkers.map((marker, i) => i < majorMarkers.length - 1 && (
                    <StyledTimeMarker key={i} $width={intervalWidth}>
                      <Typography variant="body1" sx={{ fontSize: pxToRem(12), marginLeft: '-17px', marginTop: '5px' }}>
                        {marker.label}
                      </Typography>
                    </StyledTimeMarker>
                  ))}
                  {subMarkers.map((marker, i) => {
                    const position = (marker.hour - startHour + marker.relativePosition) * (baseWidth * zoom)

                    return (
                      <StyledMinorTimeMarker key={`minor-${i}`} $left={position}>
                        <Typography variant="body1" sx={{ fontSize: pxToRem(12), opacity: 0.7, marginTop: '5px' }}>
                          {marker.label}
                        </Typography>
                      </StyledMinorTimeMarker>
                    )
                  })}
                </StyledTimelineHeader>
              </StyledTimelineContent>
            </StyledTimelineCell>
          </StyledHeaderRow>

          <StyledDataContainer>
            {isTimelineShown() && (
              <StyledTimelineIndicatorWrapper $width={timelineWidth}>
                <CurrentTimeLine
                  startHour={startHour}
                  baseWidth={baseWidth}
                  zoom={zoom}
                />
              </StyledTimelineIndicatorWrapper>
            )}

            {rows.map((row) => (
              <StyledDataRow key={row.id}>
                <StyledFixedCell>
                  <Grid container alignItems="center">
                    <Grid item xs={6} sx={{ paddingRight: 1, overflow: 'hidden' }}>
                      <Tooltip title={row.scheduleName} placement="bottom">
                        <Typography variant="body1" noWrap>{row.scheduleName}</Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <Icon
                        icon={faTruck}
                        sx={{
                          color: row.color,
                          fontSize: pxToRem(18),
                          marginRight: pxToRem(3),
                          flexShrink: 0,
                        }}
                      />
                      <Tooltip title={row.runName} placement="bottom">
                        <Typography variant="body1" noWrap>{row.runName}</Typography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </StyledFixedCell>

                <StyledTimelineCell>
                  <StyledTimelineGrid>
                    {majorMarkers.map((_, i) => i < majorMarkers.length - 1 && (
                      <StyledGridLine
                        key={i}
                        $width={intervalWidth}
                      />
                    ))}

                    {subMarkers.map((marker, i) => {
                      const position = (marker.hour - startHour + marker.relativePosition) * (baseWidth * zoom)

                      return (
                        <StyledSubGridLine
                          key={`sub-${i}`}
                          $left={position}
                        />
                      )
                    })}
                  </StyledTimelineGrid>
                  <StyledTimelineContent $width={timelineWidth}>

                    {row.timeline.travelLegs.map((leg) => (
                      <GanttLeg
                        key={leg.id}
                        color={row.color}
                        leg={leg}
                        row={row}
                        startHour={startHour}
                        baseWidth={baseWidth}
                        onStopDrop={onStopDrop}
                      />
                    ))}

                    {row.timeline.stops.map((stop) => (
                      <GanttStop
                        key={stop.id}
                        color={row.color}
                        stop={stop}
                        startHour={startHour}
                        baseWidth={baseWidth}
                      />
                    ))}
                  </StyledTimelineContent>
                </StyledTimelineCell>
              </StyledDataRow>
            ))}

            {rows.length === 0 && (
              <StyledEmptyRow>
                <StyledFixedCell>
                  <Typography variant="body1">No shift selected</Typography>
                </StyledFixedCell>
                <StyledTimelineCell sx={{ display: 'flex', alignItems: 'center', marginLeft: pxToRem(8) }}>
                  <Typography variant="body1">Select a shift to view the timeline.</Typography>
                </StyledTimelineCell>
              </StyledEmptyRow>
            )}
          </StyledDataContainer>
        </StyledTable>
      </StyledScrollContainer>

      {loading && (
        <StyledLoadingBackdrop>
          <CircularProgress size={32} />
        </StyledLoadingBackdrop>
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const StyledScrollContainer = styled('div')`
  width: 100%;
  height: 100%;
  overflow: auto;
  ${scrollbarStyle};
`

const StyledTable = styled('div')`
  display: table;
  width: max-content;
  min-width: 100%;
  background: ${props => props.theme.palette.background.paper};
`

const StyledHeaderRow = styled('div')`
  display: table-row;
  position: sticky;
  top: 0;
  z-index: 12;
  background: ${props => props.theme.palette.background.paper};
`

const StyledDataRow = styled('div')`
  display: table-row;
  
  &:hover {
    background: ${props => props.theme.palette.action.hover};
  }
`

const StyledEmptyRow = styled('div')`
  display: table-row;
`

const StyledFixedCell = styled('div')`
  display: table-cell;
  width: ${pxToRem(200)};
  min-width: ${pxToRem(200)};
  padding: ${pxToRem(8)};
  border-right: 1px solid ${props => props.theme.palette.divider};
  border-bottom: 1px solid ${props => props.theme.palette.divider};
  vertical-align: middle;
  background: ${props => props.theme.palette.background.paper};
  position: sticky;
  left: 0;
  z-index: 10;
`

const StyledTimelineCell = styled('div')`
  display: table-cell;
  position: relative;
  border-bottom: 1px solid ${props => props.theme.palette.divider};
  padding: 0;
  height: ${pxToRem(36)};
  overflow: hidden;
`

const StyledTimelineContent = styled('div') <{ $width: number }>`
  position: relative;
  width: ${props => props.$width}px;
  height: 100%;
`


const StyledTimelineHeader = styled('div')`
  display: flex;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const StyledTimeMarker = styled('div') <{ $width: number }>`
  position: relative;
  width: ${props => props.$width}px;
  padding-right: ${pxToRem(4)};
  text-align: left;
  font-weight: 500;
  height: 100%;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: -1px;
    width: 1px;
    height: ${pxToRem(10)};
    background-color: ${props => props.theme.palette.divider};
    opacity: 0.5;
  }
`

const StyledMinorTimeMarker = styled('div') <{ $left: number }>`
  position: absolute;
  left: ${props => props.$left}px;
  padding-right: ${pxToRem(4)};
  text-align: left;
  font-weight: 400;
  transform: translateX(-50%);
  height: 100%;
  
  ${props => props.$left < 30 && `
    display: none;
  `}

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 1px;
    height: ${pxToRem(10)};
    background-color: ${props => props.theme.palette.divider};
    opacity: 0.5;
  }
`

const StyledTimelineGrid = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  z-index: 0;
  pointer-events: none;
`

const StyledGridLine = styled('div') <{ $width: number }>`
  width: ${props => props.$width}px;
  height: 100%;
  border-right: 1px solid ${props => props.theme.palette.divider};
  opacity: 0.5;
`

const StyledSubGridLine = styled('div') <{ $left: number }>`
  position: absolute;
  left: ${props => props.$left}px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: ${props => props.theme.palette.divider};
  opacity: 0.5;
`

const StyledLoadingBackdrop = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background-color: ${props => props.theme.palette.background.paper};
  opacity: 0.5;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledDataContainer = styled('div')`
  position: relative;
  display: table-row-group;
`

const StyledTimelineIndicatorWrapper = styled('div') <{ $width: number }>`
  position: absolute;
  top: 0;
  left: ${pxToRem(200)};
  bottom: 0;
  width: ${props => props.$width}px;
  pointer-events: none;
  z-index: 2;
`
